import classNames from 'classnames'
import React, { useEffect, ComponentProps } from 'react'
import { useUniqueId, useResponsive, useScrollLock } from '../hooks'
import Overlay from '../overlay'
import { Portal } from '../portal'
import FocusLock from 'react-focus-lock'
import { ModalDialogContext, ModalSize } from './modal-dialog-context'
import CloseButton from '../close-button/close-button'
import { getLayerClassName } from '../utils/get-layer-class-name'

type CommonProps = {
  show: boolean
  fullscreen?: boolean
  mobileFullscreen?: boolean
  children: React.ReactNode
  ariaLabel?: string
  ariaLabelledBy?: string
  overlayProps?: Partial<ComponentProps<typeof Overlay>>
  className?: string
  boxClassName?: string
  boxStyle?: React.CSSProperties
  size?: ModalSize
  disableFocusLock?: boolean
  disableAnimation?: boolean
}

type CloseableProps = CommonProps & {
  isCloseable?: boolean
  handleClose: () => void
}

type NonCloseableProps = CommonProps & {
  isCloseable: false
  handleClose?: never
}

type Props = CloseableProps | NonCloseableProps

export const ModalDialog = ({
  show,
  fullscreen = false,
  mobileFullscreen = false,
  isCloseable = true,
  handleClose,
  children,
  ariaLabel,
  ariaLabelledBy,
  overlayProps,
  className = '',
  boxClassName,
  boxStyle,
  size = 'sm',
  disableFocusLock = false,
  disableAnimation = false,
}: Props) => {
  const generatedAriaLabelledBy = useUniqueId('modal-header-label')
  const { breakpoints } = useResponsive()

  const showFullscreen = (mobileFullscreen && breakpoints.max.SM) || fullscreen

  const SIZE_CLASSES: Record<string, string> = {
    sm: 'u-col-sm-5',
    md: 'u-col-sm-5 u-col-md-6',
    lg: 'u-col-sm-5 u-col-md-6 u-col-lg-9',
  }

  const sizeClass = showFullscreen ? 'u-w-100' : `u-w-100 ${SIZE_CLASSES[size] ?? ''}`.trim()

  const handleCloseWrapper = () => {
    if (handleClose) {
      handleClose()
    }
  }

  useScrollLock(show)

  useEffect(() => {
    const handleKeyUp = (e: KeyboardEvent): void => {
      if (isCloseable && handleClose) {
        switch (e.key) {
          case 'Escape':
            handleClose()
            break
          default:
            break
        }
      }
    }

    document.addEventListener('keyup', handleKeyUp)
    return () => {
      document.removeEventListener('keyup', handleKeyUp)
    }
  }, [isCloseable, handleClose])

  if (!show) {
    return null
  }

  return (
    <Portal className={`u-relative ${getLayerClassName('modal')}`}>
      <FocusLock returnFocus={true} disabled={disableFocusLock}>
        <ModalDialogContext.Provider
          value={{
            size,
            ariaLabelledBy: generatedAriaLabelledBy ?? ariaLabelledBy,
            handleClose: handleCloseWrapper,
          }}>
          {!showFullscreen ? <Overlay show onClick={handleCloseWrapper} {...overlayProps} /> : null}
          <div
            role="dialog"
            {...(ariaLabel
              ? { 'aria-label': ariaLabel }
              : { 'aria-labelledby': ariaLabelledBy ?? generatedAriaLabelledBy })}
            className={classNames('c-modal-dialog', className, {
              'c-dialog c-dialog--active': !showFullscreen,
              'c-dialog-fullscreen': showFullscreen,
            })}>
            {showFullscreen ? (
              <div
                tabIndex={0}
                className={classNames('c-dialog-fullscreen__content', className, sizeClass)}>
                {children}
                {isCloseable && (
                  <CloseButton className="c-modal-dialog__close-btn" onClose={handleCloseWrapper} />
                )}
              </div>
            ) : (
              <div
                className={classNames('c-dialog__container container', {
                  ['u-w-max-unset']: size === 'xl',
                })}>
                <div
                  className={classNames('c-dialog__box', boxClassName, sizeClass, {
                    'no-animation': disableAnimation,
                  })}
                  style={boxStyle}>
                  {children}
                  {isCloseable && (
                    <CloseButton
                      className="c-modal-dialog__close-btn"
                      onClose={handleCloseWrapper}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </ModalDialogContext.Provider>
      </FocusLock>
    </Portal>
  )
}
